/**
 * COMPONENT: BedOptionsStep
 * Étape ⑤ — options et consentements (cahier L88-91).
 *
 * - les options FACULTATIVES sont séparées visuellement des montants
 *   OBLIGATOIRES (« il faut séparer la partie Options qui est facultative des
 *   parties obligatoires ») ;
 * - chaque ligne peut porter un LIEN vers son document de conditions et, dans
 *   ce cas, une case « j'ai pris connaissance » OBLIGATOIRE — pour une option,
 *   elle conditionne la possibilité même de la cocher (exigence de l'assureur,
 *   cahier L91) ; pour un item obligatoire, elle doit être cochée avant de
 *   poursuivre ;
 * - les acquittements sont persistés par le socle 00166
 *   (`payment_case_acknowledgments`) à la soumission, avec l'URL AFFICHÉE en
 *   écho — c'est ce qui rend la preuve opposable.
 */

import { ExternalLink, Lock } from 'lucide-react';
import type { BedPricingItem } from '../../lib/bed/draft';
import { formatCents, isConditionsAcknowledged } from '../../lib/bed/wizard-model';
import type { FormFieldWithValues } from '../../types/form';
import { BedFieldInput } from './BedFieldInput';
import type { BedValues } from '../../lib/bed/wizard-model';

interface BedOptionsStepProps {
  mandatoryItems: BedPricingItem[];
  optionalItems: BedPricingItem[];
  selectedIds: string[];
  /** `{ [pricing_item_id]: conditions_url acquittée }`. */
  acknowledged: Record<string, string>;
  onToggleOption: (item: BedPricingItem, selected: boolean) => void;
  onToggleAck: (item: BedPricingItem, acknowledged: boolean) => void;
  /** Champs de consentement (règlement, presse, prestataires) de l'étape ⑤. */
  consentFields: FormFieldWithValues[];
  values: BedValues;
  isRequired: (field: FormFieldWithValues) => boolean;
  onChangeField: (fieldId: string, value: string) => void;
  disabled?: boolean;
}

function ConditionsLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm font-medium text-zinc-900 underline underline-offset-2 hover:text-zinc-700"
    >
      Consulter le document
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

export function BedOptionsStep({
  mandatoryItems,
  optionalItems,
  selectedIds,
  acknowledged,
  onToggleOption,
  onToggleAck,
  consentFields,
  values,
  isRequired,
  onChangeField,
  disabled = false,
}: BedOptionsStepProps) {
  return (
    <div className="space-y-8">
      {/* ---------------- Montants obligatoires ---------------- */}
      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">Inclus dans votre engagement</h3>
          <p className="text-sm text-zinc-600">
            Ces montants sont obligatoires : ils font partie de votre inscription.
          </p>
        </div>

        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
          {mandatoryItems.map((item) => {
            const acked = isConditionsAcknowledged(item, acknowledged);
            return (
              <li key={item.pricing_item_id} className="space-y-2 p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-zinc-900">{item.label}</span>
                  <span className="text-sm font-semibold text-zinc-900">
                    {formatCents(item.amount_cents)}
                  </span>
                </div>

                {item.conditions_url && (
                  <div className="space-y-2 rounded-md bg-zinc-50 p-3">
                    <ConditionsLink url={item.conditions_url} />
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id={`ack-${item.pricing_item_id}`}
                        checked={acked}
                        disabled={disabled}
                        onChange={(event) => onToggleAck(item, event.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                      />
                      <label
                        htmlFor={`ack-${item.pricing_item_id}`}
                        className="cursor-pointer text-sm text-zinc-700"
                      >
                        J’ai pris connaissance du document
                        <span className="ml-1 text-red-500">*</span>
                      </label>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
          {mandatoryItems.length === 0 && (
            <li className="p-4 text-sm text-zinc-500">Aucun montant obligatoire configuré.</li>
          )}
        </ul>
      </section>

      {/* ---------------- Options facultatives ---------------- */}
      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">Options facultatives</h3>
          <p className="text-sm text-zinc-600">
            Libre à vous de les ajouter — elles s’ajoutent au total de votre récapitulatif.
          </p>
        </div>

        {optionalItems.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-200 p-4 text-sm text-zinc-500">
            Aucune option n’est proposée pour cette édition.
          </p>
        ) : (
          <ul className="space-y-3">
            {optionalItems.map((item) => {
              const acked = isConditionsAcknowledged(item, acknowledged);
              const selected = selectedIds.includes(item.pricing_item_id);
              // Exigence assureur (cahier L91) : on ne peut pas cocher l'option
              // tant que le document n'a pas été acquitté.
              const lockedByAck = Boolean(item.conditions_url) && !acked;

              return (
                <li
                  key={item.pricing_item_id}
                  className={`space-y-3 rounded-lg border p-4 ${
                    selected ? 'border-zinc-900 bg-zinc-50/60' : 'border-zinc-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id={`option-${item.pricing_item_id}`}
                      checked={selected}
                      disabled={disabled || lockedByAck}
                      onChange={(event) => onToggleOption(item, event.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 disabled:opacity-50"
                    />
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor={`option-${item.pricing_item_id}`}
                        className={`flex flex-wrap items-baseline justify-between gap-2 text-sm ${
                          lockedByAck ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                      >
                        <span className="font-medium text-zinc-900">{item.label}</span>
                        <span className="font-semibold text-zinc-900">
                          {formatCents(item.amount_cents)}
                        </span>
                      </label>

                      {lockedByAck && (
                        <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-amber-700">
                          <Lock className="h-3.5 w-3.5" />
                          Prenez connaissance du document avant de pouvoir cocher cette option.
                        </p>
                      )}
                    </div>
                  </div>

                  {item.conditions_url && (
                    <div className="space-y-2 rounded-md bg-white p-3 ring-1 ring-zinc-100">
                      <ConditionsLink url={item.conditions_url} />
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          id={`ack-${item.pricing_item_id}`}
                          checked={acked}
                          disabled={disabled}
                          onChange={(event) => onToggleAck(item, event.target.checked)}
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                        />
                        <label
                          htmlFor={`ack-${item.pricing_item_id}`}
                          className="cursor-pointer text-sm text-zinc-700"
                        >
                          J’ai pris connaissance du document
                          <span className="ml-1 text-red-500">*</span>
                        </label>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ---------------- Consentements ---------------- */}
      {consentFields.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-base font-semibold text-zinc-900">Consentements</h3>
          <div className="space-y-1 rounded-lg border border-zinc-200 p-4">
            {consentFields.map((field) => (
              <BedFieldInput
                key={field.id}
                field={field}
                values={values}
                required={isRequired(field)}
                disabled={disabled}
                onChange={onChangeField}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
